import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { LinkButton } from '@onefootprint/ui';
import type React from 'react';
import { Fragment } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useField from '../../hooks/use-field';
import type { DiField } from '../../vault.types';
import Field from '../field';
export type FieldsetProps = WithEntityProps & {
  children?: React.ReactNode;
  fields: DiField[];
  iconComponent: Icon;
  title: string;
};
const Fieldset = t0 => {
  const $ = _c(40);
  const {
    children,
    entity,
    fields,
    iconComponent: IconComponent,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== decryptForm.control) {
    t3 = {
      control: decryptForm.control,
      name: "fields",
      defaultValue: t2
    };
    $[2] = decryptForm.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedFields = useWatch(t3);
  const dis = fields.map(_temp);
  const getFieldProps = useField(entity);
  let t4;
  if ($[4] !== getFieldProps) {
    t4 = di => getFieldProps(di).canSelect;
    $[4] = getFieldProps;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectableFields = dis.filter(t4);
  let t5;
  if ($[6] !== selectedFields) {
    t5 = field_0 => selectedFields.includes(field_0);
    $[6] = selectedFields;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const allSelected = selectableFields.every(t5);
  const hasSelectableFields = selectableFields.length > 0;
  const shouldShowSelectAll = decryptControls.step === "selecting-fields" && hasSelectableFields;
  let t6;
  if ($[8] !== decryptForm || $[9] !== selectableFields) {
    t6 = () => {
      decryptForm.setFields(selectableFields);
    };
    $[8] = decryptForm;
    $[9] = selectableFields;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleSelectAll = t6;
  let t7;
  if ($[11] !== decryptForm || $[12] !== selectableFields) {
    t7 = () => {
      decryptForm.unsetFields(selectableFields);
    };
    $[11] = decryptForm;
    $[12] = selectableFields;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleDeselectAll = t7;
  let t8;
  if ($[14] !== entity) {
    t8 = field_1 => {
      const {
        di: di_0,
        renderCustomField
      } = field_1;
      return renderCustomField ? <Fragment key={di_0}>{renderCustomField({
          entity,
          di: di_0
        })}</Fragment> : <Field key={di_0} di={di_0} entity={entity} />;
    };
    $[14] = entity;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const renderField = t8;
  let t9;
  if ($[16] !== IconComponent) {
    t9 = <IconComponent />;
    $[16] = IconComponent;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9 || $[19] !== title) {
    t10 = <h2 className="flex items-center gap-2 text-label-2 text-primary">{t9}{title}</h2>;
    $[18] = t9;
    $[19] = title;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== allSelected || $[22] !== handleDeselectAll || $[23] !== handleSelectAll || $[24] !== shouldShowSelectAll || $[25] !== t) {
    t11 = shouldShowSelectAll && <LinkButton onClick={allSelected ? handleDeselectAll : handleSelectAll}>{allSelected ? t(_temp2) : t(_temp3)}</LinkButton>;
    $[21] = allSelected;
    $[22] = handleDeselectAll;
    $[23] = handleSelectAll;
    $[24] = shouldShowSelectAll;
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <header className="flex justify-between px-4 py-2 border-b border-solid border-tertiary rounded-t-[sm] bg-secondary h-[40px]">{t10}{t11}</header>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== children || $[31] !== fields || $[32] !== renderField) {
    t13 = children || fields.map(renderField);
    $[30] = children;
    $[31] = fields;
    $[32] = renderField;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13) {
    t14 = <div className="flex flex-col gap-3 p-4 flex-1">{t13}</div>;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t12 || $[37] !== t14 || $[38] !== title) {
    t15 = <fieldset aria-label={title} className="w-full h-full flex flex-col justify-between border border-solid border-tertiary rounded-sm overflow-hidden">{t12}{t14}</fieldset>;
    $[36] = t12;
    $[37] = t14;
    $[38] = title;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default Fieldset;
function _temp(field) {
  return field.di;
}
function _temp2($) {
  return $.deselectAll;
}
function _temp3($_0) {
  return $_0.selectAll;
}